import {
  IonHeader,
  IonIcon,
  IonList,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  type RefresherEventDetail,
  useIonRouter,
  useIonViewWillEnter,
} from "@ionic/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { calendarOutline } from "ionicons/icons";
import { DateHeader } from "@/components/atoms/date-header";
import { Content } from "@/components/content";
import { EmptyState } from "@/components/molecules/empty-state";
import { OrganizationDropdown } from "@/components/molecules/organization/organization-dropdown";
import { DiscoverCalendar } from "@/components/molecules/project/discover-calendar";
import { DiscoverFilterBar } from "@/components/molecules/project/discover-filter-bar";
import { DiscoverRow } from "@/components/molecules/project/discover-row";
import {
  DiscoverSignupSheet,
  projectRouteForCard,
} from "@/components/molecules/project/discover-signup-sheet";
import { ProjectCardCarousel } from "@/components/molecules/project/project-card.featured.carousel";
import { Toolbar } from "@/components/toolbar";
import { api } from "@/lib/backend";
import { type Project, type ProjectEmployeeDayCard } from "@/lib/backend/api";
import { extractionWrapper } from "@/lib/backend/utils/extract";
import {
  type DiscoverFilters,
  EMPTY_DISCOVER_FILTERS,
  activeFilterCount,
  applyDiscoverFilters,
  discoverFacets,
} from "@/lib/discover-facets";
import {
  type DayKey,
  addMonths,
  lastDayOfMonth,
  monthOf,
  openDiscoverWindow,
  shiftMonths,
  todayKey,
} from "@/lib/discover-window";
import { useAppReworkFlags } from "@/lib/hooks/use-app-rework-flags";
import { useDiscoverDayStates } from "@/lib/hooks/use-discover-day-states";
import { queryOptions_projects_employee_day_range } from "@/lib/requests/project/project.employee-day-range";
import { cn } from "@/lib/style/cva.config";
import { dayKeyToDate } from "@/utils/format-datetime";

/** The day a card sits on, in the one form that cannot drift — see `discover-window.ts`. */
function dayOf(card: ProjectEmployeeDayCard): DayKey {
  return card.day.split("T")[0];
}

/**
 * How far _below the pinned calendar_ a day heading may sit and still count as the day you are on.
 *
 * The calendar reads the list rather than the other way round, so it needs a line to read at. That
 * line is the calendar's own bottom edge — everything above it is behind the calendar and not being
 * read at all — plus a little slack, so a heading that has only just slid under does not flip the
 * strip a week onwards before the reader has seen it.
 */
const READING_BAND = 24;

/**
 * How long a jump takes.
 *
 * Long enough to see which way you went — an instant cut leaves you somewhere else with no sense of
 * having travelled — and short enough that it never feels like waiting for the screen.
 */
const JUMP_DURATION = 300;

/**
 * Ontdek.
 *
 * Everything on this screen is bounded by one thing: the date range it asks for. The endpoint
 * behind it takes a `pagination` argument and ignores it — see `discover-window.ts` — so the window
 * _is_ the page size, and it used to be two years.
 *
 * ## The calendar moves the list, it does not cut it
 *
 * Tapping a day scrolls the list to that day. It used to filter the list down to it, which made the
 * screen answer a question nobody asked: an employee scanning for work wants to _get to_ Saturday,
 * not to be left alone with Saturday. Filtering also meant every tap had to be undone, and it hid
 * the thing a marketplace is for — the shift on the day next to the one you were looking at.
 *
 * So the list holds the whole fetched window at once and the calendar is a jump control. The two
 * stay in step in both directions: tapping a day scrolls there, and scrolling sets the day the
 * calendar stands on. That second half is what keeps the strip from lying about where you are.
 *
 * And the calendar stays pinned to the top with the list running underneath it, because a control
 * for the thing below it has to remain reachable while you read that thing. Everything that follows
 * from that — where a jump has to stop so the day is not parked behind the calendar, where the line
 * is that decides which day you are on — is measured off its live height, never a constant.
 */
export function ProjectsReworkPage() {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState("");
  const router = useIonRouter();

  // Two sub-flags live on this screen. The redesign is one thing; how far ahead it looks and
  // whether it opens with a recommendation are two others, each with its own way of being wrong.

  // One "now" for the life of this view. It feeds the query keys through the window, so a live
  // `new Date()` would build a new key on every render and refetch in a loop.
  const [now] = useState(() => new Date());
  const today = useMemo(() => todayKey(now), [now]);

  // The day the list is showing at the top, and so the day the calendar stands on. Alleen dat: de
  // kalender brengt je naar een dag, en wat er opgehaald wordt hangt er niet van af.
  const [anchor, setAnchor] = useState<DayKey>(today);
  const [calendarExpanded, setCalendarExpanded] = useState(false);
  const [filters, setFilters] = useState<DiscoverFilters>(EMPTY_DISCOVER_FILTERS);

  // The row that was tapped, or null. Signing up happens here rather than on the project screen so
  // the list — and the place you had in it — survives the decision.
  const [signupCard, setSignupCard] = useState<ProjectEmployeeDayCard | null>(null);

  const contentRef = useRef<HTMLIonContentElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLIonHeaderElement>(null);
  /** A jump asked for before the day it points at had been fetched. Consumed once it arrives. */
  const pendingJump = useRef<DayKey | null>(null);
  /** True while a jump is animating, so the calendar does not read its own scrolling back. */
  const isJumping = useRef(false);

  /**
   * How much of the top of the scroller the header is covering.
   *
   * The scroller sits _behind_ the header — `.inner-scroll` is absolutely placed at `top:
   * calc(var(--offset-top) * -1)` — so a heading scrolled to `0` parks behind the glass and the
   * jump looks like it did nothing.
   *
   * Measured rather than written down, because it is not one number: the calendar inside the header
   * is one row collapsed and six open, and it changes under the reader's thumb.
   */
  const pinnedHeight = () => headerRef.current?.offsetHeight ?? 0;

  const visibleMonth = useMemo(() => monthOf(anchor), [anchor]);

  /**
   * Alles wat openstaat, in één verzoek.
   *
   * Hier stond een venster omheen -- de zichtbare maand plus twee, dat meegroeide als je vooruit
   * bladerde -- met een vlag eromheen om het per organisatie aan te zetten. Dat is eruit. Het
   * scheelde een paar honderd kaarten, maar het besliste ook wélk werk een medewerker kán zien, en
   * dat is geen instelling: wie een dienst openzet wil dat hij gevonden wordt, ook vijf maanden
   * vooruit.
   *
   * De lijst houdt daarmee de hele horizon vast, en dat is precies wat de kalender nodig heeft: de
   * stippen komen weer uit de lijst zelf, zonder een tweede vraag aan de server om de gaten te
   * vullen.
   */
  const window = useMemo(() => openDiscoverWindow(now), [now]);

  const featuredFilter = useMemo(
    () => [
      {
        AND: [
          {
            startDate: {
              operator: "gte" as const,
              value: window.from.toISOString(),
            },
          },
          {
            startDate: {
              operator: "lte" as const,
              value: window.to.toISOString(),
            },
          },
          {
            isFeatured: {
              operator: "equals" as const,
              value: true,
            },
          },
          ...(searchText
            ? [
                {
                  name: {
                    operator: "contains" as const,
                    value: searchText,
                  },
                },
              ]
            : []),
        ],
      },
    ],
    [searchText, window],
  );

  // Uitgelicht haalt zichzelf alleen op als er niets gefilterd is. Wie heeft gefilterd heeft
  // gezegd wat hij zoekt; een redactionele selectie is dan ruis, en een tweede request voor niets.
  const showFeatured = activeFilterCount(filters) === 0 && !searchText;

  const { data: featuredProjectsData, refetch: refetchFeatured } = useQuery({
    enabled: showFeatured,
    ...queryOptions_projects_employee_day_range({
      filter: featuredFilter,
      sort: {
        field: "startDate",
        order: "asc",
      },
      // The endpoint ignores this. Kept, and kept generous, purely as a guard for the day it
      // starts honouring pagination — a silently truncated marketplace is worse than a slow one.
      pagination: {
        take: 500,
        page: 1,
      },
    }),
  });

  const upcomingFilter = useMemo(
    () => [
      {
        AND: [
          {
            startDate: {
              operator: "gte" as const,
              value: window.from.toISOString(),
            },
          },
          {
            startDate: {
              operator: "lte" as const,
              value: window.to.toISOString(),
            },
          },
          ...(searchText
            ? [
                {
                  name: {
                    operator: "contains" as const,
                    value: searchText,
                  },
                },
              ]
            : []),
        ],
      },
    ],
    [searchText, window],
  );

  const { data: projectsData, refetch: refetchProjects } = useQuery({
    ...queryOptions_projects_employee_day_range({
      filter: upcomingFilter,
      sort: {
        field: "startDate",
        order: "asc",
      },
      // Same: ignored today. See `discover-window.ts` — the date range is what bounds this.
      pagination: {
        take: 1000,
        page: 1,
      },
    }),
  });

  // Days you already applied for stay in the list, marked, instead of vanishing.
  //
  // `canApply` is `!employeeHasApplied` on the server, and `employeeHasApplied` is true when you
  // applied to *any* shift that day. Dropping those cards therefore took the whole day away — you
  // put your name down for the bar and lost sight of the kitchen shift beside it, on a screen
  // whose entire job is showing you what is still open.
  const availableCards = useMemo(() => projectsData?.items ?? [], [projectsData?.items]);

  // Function and city are filtered here rather than by the server, which has no filter key for
  // either. Honest only because the window contains the period: nothing was cut off before this ran.
  //
  // The calendar's "work is open here" marks follow the filters too — set "Bar" and the days
  // without bar work go quiet instead of pointing at a day whose rows you filtered away yourself.
  const visibleCards = useMemo(
    () => applyDiscoverFilters(availableCards, filters),
    [availableCards, filters],
  );

  /**
   * De dagen waarop iets te doen is, rechtstreeks uit de lijst.
   *
   * Dit liep een tijd via een apart dagoverzicht van de server (`employee-day-summary`), omdat de
   * lijst maar een paar maanden ver reikte en de kalender daarbuiten blind was. Nu de lijst de hele
   * horizon vasthoudt is dat overzicht overbodig -- en een stip die uit de lijst komt volgt
   * bovendien vanzelf de filters die hier in de app draaien, zonder dat iets iets belooft wat de
   * lijst niet waarmaakt.
   */
  const availableDays = useMemo(() => new Set(visibleCards.map(dayOf)), [visibleCards]);

  // What is going on for *you* per day — applied, planned in, asked to be let off. Only asked for
  // once there is a calendar to paint it on.
  const dayStates = useDiscoverDayStates({
    window,
    availableDays,
  });

  // Counted over the whole window the list holds, so the numbers describe the same period the list
  // does, and over the *unfiltered* cards, so a choice never offers a dead end.
  const facets = useMemo(() => discoverFacets(availableCards, filters), [availableCards, filters]);

  // Transform featured cards to projects for the carousel
  // Group by projectId and take the first occurrence to get project details
  const featuredProjects = useMemo(() => {
    const cards = featuredProjectsData?.items ?? [];
    // Filter by canApply first
    const applicableCards = cards.filter((card) => card.canApply);
    const projectMap = new Map<string, ProjectEmployeeDayCard>();

    // Group by projectId, keeping only the first (earliest) card per project
    applicableCards.forEach((card) => {
      if (!projectMap.has(card.projectId)) {
        projectMap.set(card.projectId, card);
      }
    });

    // Transform to Project objects
    return Array.from(projectMap.values()).map(
      (card): Project => ({
        id: card.projectId,
        name: card.projectName,
        image: card.projectImage,
        startDate: new Date(card.day).toISOString(),
        status: card.projectStatus,
        stats: {
          uniqueUserFunctions: card.availableFunctions,
          totalCapacity: 0,
          totalApplications: 0,
          totalShifts: 0,
          filledShifts: 0,
        },
        // Add other required Project fields with defaults
        endDate: null,
        description: null,
        isArchived: false,
        isPublished: card.isPublished,
        isFeatured: true, // We know this is true because we filtered for it
        createdAt: "",
        updatedAt: "",
        schedulingMethod: "",
        signupIsScheduling: false,
        allowEmployeeSubmitHours: false,
        allowEmployeeSubmitDeclarations: false,
        enableQRCodeClocking: false,
        timeZone: null,
      }),
    );
  }, [featuredProjectsData?.items]);

  // Group all cards by day
  const groupedProjects = useMemo(() => {
    // Group by day
    const groups: Record<string, ProjectEmployeeDayCard[]> = {};

    visibleCards.forEach((card) => {
      const cardDay = dayOf(card);
      if (!groups[cardDay]) {
        groups[cardDay] = [];
      }
      groups[cardDay].push(card);
    });

    // Sort dates and map to expected structure
    return Object.keys(groups)
      .sort()
      .map((dateKey) => ({
        day: dateKey,
        // `new Date("2026-08-14")` is middernacht UTC en valt westelijk van Greenwich terug naar
        // de 13e — dan las de dagkop een dag eerder dan de kalendercel die net was aangetikt. De
        // middag-truc die daarvoor in de plaats kwam rekende in de klok van het toestel en schoof
        // aan de randen (UTC+14, UTC-11) alsnog een dag op; `dayKeyToDate` levert de echte
        // middernacht in dezelfde zone waarin `DateHeader` de kop afdrukt.
        date: dayKeyToDate(dateKey),
        items: groups[dateKey],
      }));
  }, [visibleCards]);

  /**
   * Scroll to the day a jump was asked for.
   *
   * The nearest day _at or after_ the one asked for, because an empty Tuesday still has to lead
   * somewhere — landing on Wednesday is what "take me to Tuesday" means when Tuesday holds nothing.
   * When nothing at all comes after it, the end of the list is as far as "later" goes, and stopping
   * there beats a tap that does nothing.
   *
   * An empty list is the one case worth waiting on: the request stays pending until rows arrive, so
   * a jump made while the first page is still in flight lands once it does.
   */
  const consumeJump = useCallback(async () => {
    const day = pendingJump.current;
    const content = contentRef.current;
    const list = listRef.current;
    if (!day || !content || !list) {
      return;
    }

    const headings = Array.from(list.querySelectorAll<HTMLElement>("[data-day]"));
    if (headings.length === 0) {
      return;
    }

    // Document order is chronological, so the first match is the nearest day forward.
    const target =
      headings.find((element) => (element.dataset.day ?? "") >= day) ??
      headings[headings.length - 1];

    pendingJump.current = null;
    const scroller = await content.getScrollElement();
    // Measured here rather than before the await: tapping a day in the open month closes it, and by
    // now that has happened — so both the heading's position and the calendar's height are the ones
    // the reader will actually be looking at.
    const top =
      target.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top +
      scroller.scrollTop -
      pinnedHeight();

    // Glide, and hold the calendar still while it glides.
    //
    // The scroll and the strip read each other: scrolling sets the day the calendar stands on, so an
    // animated jump across three months would flip the strip through every week on the way and land
    // shuddering. `isJumping` takes the calendar out of that loop for the length of the animation —
    // it already knows which day it is going to, and nothing it sees in between is worth believing.
    isJumping.current = true;
    try {
      await content.scrollToPoint(0, Math.max(0, top), JUMP_DURATION);
    } finally {
      isJumping.current = false;
    }
  }, []);

  const jumpToDay = useCallback(
    (day: DayKey) => {
      setAnchor(day);
      pendingJump.current = day;
      void consumeJump();
    },
    [consumeJump],
  );

  // A jump that outran the data: once the wider window lands, the day it was pointing at exists.
  useEffect(() => {
    void consumeJump();
  }, [groupedProjects, consumeJump]);

  /**
   * The other direction: scrolling sets the day the calendar stands on.
   *
   * The observer only decides _when_ to look; the answer is recomputed from every heading, because
   * an entry list tells you which headings crossed the band, not which one you ended up under.
   */
  useEffect(() => {
    const content = contentRef.current;
    const list = listRef.current;
    if (!content || !list) {
      return;
    }

    let observer: IntersectionObserver | undefined;
    let cancelled = false;

    void content.getScrollElement().then((scroller) => {
      if (cancelled) {
        return;
      }

      const settle = () => {
        if (isJumping.current) {
          return;
        }
        const headings = Array.from(list.querySelectorAll<HTMLElement>("[data-day]"));
        if (headings.length === 0) {
          return;
        }
        const scrollerTop = scroller.getBoundingClientRect().top;
        const line = pinnedHeight() + READING_BAND;
        let current = headings[0].dataset.day;
        for (const heading of headings) {
          if (heading.getBoundingClientRect().top - scrollerTop > line) {
            break;
          }
          current = heading.dataset.day;
        }
        if (current) {
          setAnchor(current);
        }
      };

      // No `rootMargin` narrowing this to a band. It would have to be expressed against the
      // scroller's height, and that height is zero for as long as the page has not been laid out —
      // which turns the margin into `-Infinity%` and makes the constructor throw. Waking on every
      // heading that enters or leaves the scroller costs a handful of extra calls to a function that
      // reads rects and nothing else.
      observer = new IntersectionObserver(settle, { root: scroller });
      list.querySelectorAll<HTMLElement>("[data-day]").forEach((heading) => {
        observer?.observe(heading);
      });
    });

    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, [groupedProjects]);

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await Promise.all([refetchFeatured(), refetchProjects(), dayStates.refetch()]);
    event.detail.complete();
  };

  // Refetch projects when navigating back to this page
  useIonViewWillEnter(() => {
    void refetchFeatured();
    void refetchProjects();
    void dayStates.refetch();
  });

  return (
    <IonPage>
      <IonHeader ref={headerRef}>
        <Toolbar className="ion-no-border plt-desktop:sm:ion-px-[max(calc(calc(100vw-640px)/2),theme(spacing.4))]">
          {/* Zoeken stond hier, tussen de titel en de organisatie. Dat is de regel voor waar je
              bent en bij wie -- niet voor wat je zoekt. Het staat nu bij de andere twee vragen die
              de lijst inperken, op de filterregel. */}
          <div className="flex items-center gap-1">
            <h1 className="text-2xl font-semibold leading-[1.2] text-txt-primary">
              {t("routes:tabs.discover")}
            </h1>

            <OrganizationDropdown />
          </div>
        </Toolbar>

        {/* De kalender staat in de kop en niet als `sticky` blok in de lijst. Het was allebei een
            poging tot hetzelfde -- een besturing die blijft staan terwijl je eronder leest -- maar
            het glas werd er lelijk van: twee halfdoorzichtige panelen boven elkaar tekenen ieder
            hun eigen vervaging, en op de naad zie je dat. In de kop is het één vlak.

            De kalender staat er in beide standen, ook zonder de window-vlag. Hij deed altijd twee
            dingen -- het venster verschuiven én de lijst naar een dag springen -- en alleen dat
            eerste is zinloos bij een vaste periode van twee jaar. Dat tweede is er juist meer waard:
            alles staat al in het geheugen en er is veel meer om doorheen te springen.

            Uitgelicht staat onder de kalender in plaats van erboven. Dat blijft het eerste
            stuk inhoud van het scherm; de kalender is geen inhoud maar de knop waarmee je erdoorheen
            loopt, en die hoort bij de kop.

            Ionic meet de hoogte van de kop bij het laden en verder alleen bij een `resize`, dus een
            kalender die van week naar maand klapt zou de bovenkant van de lijst verstoppen. Die
            meting wordt bijgestuurd -- zie de schildwacht in `components/content.tsx`. */}
        <div className="px-4 pb-2 plt-desktop:sm:px-[max(calc(calc(100vw-640px)/2),theme(spacing.4))]">
          <DiscoverCalendar
            anchor={anchor}
            onJump={jumpToDay}
            states={dayStates.states}
            expanded={calendarExpanded}
            onExpandedChange={setCalendarExpanded}
            today={today}
          />
        </div>
      </IonHeader>
      {/* `ion-pt-0` is what lets the calendar reach the top.
          `ion-padding` puts 16px of padding on the scroller, and a sticky child is constrained to
          its containing block — the scroller's *content* box, which starts below that padding. So
          `top-0` could only ever reach 16px down, and rows slid past uncovered in the strip above
          it. The padding is handed to the first block instead, which is where it was doing its
          visible work anyway. */}
      <Content scrollUnderChrome ref={contentRef} className="ion-pt-0">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent refreshingSpinner="circles" />
        </IonRefresher>

        {/* Uitgelicht komt vóór de kalender, niet erna. Het is de enige redactionele keuze op dit
            scherm — wat de organisatie zelf naar voren schuift — en die hoort de eerste te zijn die
            je ziet, niet iets dat je pas tegenkomt nadat je langs een kalender en een filterbalk
            bent gescrold. */}
        {showFeatured && featuredProjects.length > 0 && (
          <div className="flex flex-col gap-2 pt-4">
            {/* A plain heading, where this used to be an `IonHeader collapse="condense"`. That is the
                iOS large-title construct, and it means something specific as the first child of an
                ion-content: it pairs with the page header and scales on scroll. It got away with
                being a section label halfway down the page; at the top it would start behaving like
                the thing it actually is. */}
            <h2 className="text-xl font-semibold text-txt-primary">{t("common:featured.title")}</h2>

            <IonList className="flex flex-row gap-4 !ion-mt-0 !ion-pt-0">
              <ProjectCardCarousel projects={featuredProjects} />
            </IonList>
          </div>
        )}

        <DiscoverFilterBar
          filters={filters}
          onFiltersChange={setFilters}
          functions={facets.functions}
          cities={facets.cities}
          search={searchText}
          onSearchChange={setSearchText}
        />

        {/* Grouped upcoming projects */}
        {groupedProjects.length > 0 ? (
          <div ref={listRef} className="flex flex-col gap-4">
            {groupedProjects.map((group) => (
              <div key={group.day} data-day={group.day}>
                <DateHeader date={group.date} />
                <IonList className="flex flex-col">
                  {group.items.map((card, index) => (
                    <DiscoverRow
                      key={`${card.projectId}-${card.day}-${index}`}
                      card={card}
                      onSelect={setSignupCard}
                    />
                  ))}
                </IonList>
              </div>
            ))}
          </div>
        ) : (
          /* Was hardcoded Dutch in the component, in a white-label app. And it said the same
             thing whether the period was empty, the filters were too narrow, or the search
             found nothing — three different problems with three different ways out. */
          <div className="flex flex-col items-center gap-4">
            <EmptyState
              icon={calendarOutline}
              title={t("common:discover.empty.title")}
              description={
                searchText
                  ? t("common:discover.empty.searched")
                  : activeFilterCount(filters) > 0
                    ? t("common:discover.empty.filtered")
                    : t("common:discover.empty.description")
              }
            />

            {/* Hier stond "Kijk een maand verder". Die knop hoorde bij het venster: de lijst reikte
                maar een paar maanden ver, dus leeg kon ook betekenen "je kijkt niet ver genoeg".
                Nu de lijst alles vasthoudt wat openstaat is leeg gewoon leeg, en een knop die
                belooft dat er verderop iets is zou liegen. */}
          </div>
        )}

        <DiscoverSignupSheet
          card={signupCard}
          onDismiss={() => setSignupCard(null)}
          onOpenProject={(card) => {
            setSignupCard(null);
            router.push(projectRouteForCard(card), "forward");
          }}
        />
      </Content>
    </IonPage>
  );
}
